"use client"

import type React from "react"
import { useState, useEffect, useMemo } from "react"
import { X, UserPlus } from "lucide-react"
import type { DynamicUser, DynamicSchema, DynamicFormData, DynamicCreateRequest } from "../types/dynamic"
import { createDynamicUser } from "../services/api"
import { addToQueue } from "../services/queue"
import { addDynamicUserToDB } from "../services/database"
import { SchemaService } from "../services/schema"
import { useNetworkStatus } from "../hooks/useNetworkStatus"
import { AccordionField } from "./AccordionField"
import { BadgeColorTable } from "./BadgeColorTable"

interface DynamicRegisterModalProps {
  schema: DynamicSchema
  existingUsers: DynamicUser[]
  onClose: () => void
  onSuccess: (newUser: DynamicUser) => void
  showToast: (type: "success" | "error" | "info", message: string) => void
  cancelCurrentFetch: () => void
}

// Define las propiedades del acordeón
type LocalAccordion = {
  name: string
  label: string
  position: number
  content: React.ReactNode
}

const LOCAL_ACCORDIONS: LocalAccordion[] = [
  {
    name: "ac_info_general",
    label: "Información general de los colores del gafete",
    position: 0,
    content: (
      <>
        <BadgeColorTable />
      </>
    ),
  },
]

const DynamicRegisterModal: React.FC<DynamicRegisterModalProps> = ({
  schema,
  existingUsers,
  onClose,
  onSuccess,
  showToast,
  cancelCurrentFetch,
}) => {
  const [loading, setLoading] = useState(false)
  const isOnline = useNetworkStatus()
  
  // Obtener campos de creación del schema
  const creationFields = SchemaService.getCreationFields(schema)
  
  // Inicializar datos del formulario
  const [formData, setFormData] = useState<DynamicFormData>(() => {
    const rawFormData = SchemaService.initializeFormData(schema, existingUsers)

    // Normalizar valores booleanos del formulario
    const normalizedFormDataBoolean = Object.keys(rawFormData).reduce((acc, key) => {
      const fieldConfig = schema[key]
      if (fieldConfig?.type === "boolean") {
        acc[key] = rawFormData[key] === "1" || rawFormData[key] === true
      } else {
        acc[key] = rawFormData[key]
      }
      return acc
    }, {} as DynamicFormData)

    return normalizedFormDataBoolean
  })
  
  const [errors, setErrors] = useState<{ [field: string]: string }>({})

  // Regenerar valores únicos cuando cambian los usuarios existentes
  useEffect(() => {
    const newFormData = { ...formData }
    let hasChanges = false

    creationFields.forEach(({ fieldName, fieldSchema }) => {
      if (fieldSchema.defaultValue === 'randomUnique') {
        const newValue = SchemaService.generateUniqueValue(fieldName, existingUsers)
        if (newValue !== formData[fieldName]) {
          newFormData[fieldName] = newValue
          hasChanges = true
        }
      }
    })

    if (hasChanges) {
      setFormData(newFormData)
    }
  }, [existingUsers.length])

//---------------------------------- ACORDEON ---------------------------------------
  // Construir la lista de campos que vas a renderizar (schema + acordeones)
  const creationFieldsFromSchema = SchemaService.getCreationFields(schema)

  const creationFieldsWithAccordions = useMemo(() => {
    const BEFORE_FIELDS = ["badge_color", "categoria"]

    const minMarkerPos = Math.min(
      ...BEFORE_FIELDS.map(n => schema[n]?.position ?? 999)
    )

    const accordionAsFields = LOCAL_ACCORDIONS.map(ac => ({
      fieldName: ac.name,
      fieldSchema: {
        type: "accordion",
        label: ac.label,
        position: (minMarkerPos === 999 ? 0 : minMarkerPos - 0.001),
        editable: false,
        required: false,
        visible: true,
      },
    }))

    return [...accordionAsFields, ...creationFieldsFromSchema].sort((a, b) => {
      const pa = a.fieldSchema.position ?? 999
      const pb = b.fieldSchema.position ?? 999
      if (pa !== pb) return pa - pb
      // Si la posición es igual, priorizar acordeones
      if (a.fieldSchema.type === "accordion" && b.fieldSchema.type !== "accordion") return -1
      if (b.fieldSchema.type === "accordion" && a.fieldSchema.type !== "accordion") return 1
      return 0
    })
  }, [schema])
//-------------------------------------------------------------------------------------

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    let processedValue: any = value

    // Procesar valor según el tipo de campo
    const fieldConfig = schema[name]
    if (fieldConfig) {
      switch (fieldConfig.type) {
        case 'number':
          processedValue = value === '' ? 0 : Number(value)
          break
        case 'boolean':
          processedValue = (e.target as HTMLInputElement).checked
          break
        case 'string':
        case 'enum':
        default:
          // Normalización específica por campo
          if (name === 'email') {
            processedValue = value.toLowerCase().trim()
          } else if (name === 'telefono') {
            processedValue = value.replace(/[^\d\s()+-]/g, "")
          } else if (name === 'nombre' || name === 'apellido') {
            processedValue = value.replace(/\s+/g, " ")
          } else {
            processedValue = value
          }
          break
      }
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }))

    // Limpiar error cuando el usuario comience a escribir
    if (errors[name]) {
      setErrors(prev => {
        const { [name]: removedError, ...rest } = prev
        return rest
      })
    }
  }

  const validateForm = (): boolean => {
    const validationErrors = SchemaService.validateFormData(formData, schema)
    const errorMap: { [field: string]: string } = {}

    validationErrors.forEach(error => {
      errorMap[error.field] = error.message
    })

    setErrors(errorMap)
    return validationErrors.length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      showToast("error", "Por favor corrige los errores en el formulario")
      return
    }

    // Cancelar cualquier descarga en curso
    cancelCurrentFetch()
    setLoading(true)

    try {
      // Formatear datos para la API
      const { mainFields, extraFields } = SchemaService.formatForApi(formData)
      
      const createRequest: DynamicCreateRequest = {
        //folio: mainFields.folio,
        name: mainFields.name,
        last_name: mainFields.last_name,
        email: mainFields.email,
        phone_number: mainFields.phone_number,
        printed: mainFields.printed || 0,
        print_date: mainFields.print_date,
        number_of_prints: mainFields.number_of_prints || 0,
        extra: extraFields
      }

      // Obtener campo único del schema
      const uniqueField = SchemaService.getUniqueField(schema)
      const uniqueValue = uniqueField ? SchemaService.getFieldValue({...mainFields, extra: extraFields}, uniqueField) : null

      // Crear objeto de usuario para almacenamiento local temporal
      const tempUser: DynamicUser = {
        id: Date.now(), // ID temporal para visualización
        name: mainFields.name,
        last_name: mainFields.last_name,
        email: mainFields.email,
        phone_number: mainFields.phone_number,
        printed: 0,
        extra: extraFields
      }

      console.log(`💾 Guardando usuario temporal con campo único ${uniqueField}: ${uniqueValue}`)

      // Guardar localmente primero (actualización optimista)
      await addDynamicUserToDB(tempUser)
      onSuccess(tempUser)
      onClose() // Cerrar modal inmediatamente

      // Manejar creación en la nube en segundo plano
      const syncCreation = async () => {
        if (isOnline) {
          try {
            console.log("💾 Estoy entrando al servidor para guardar los datos")
            const result = await createDynamicUser(createRequest)
            
            // Verificar si es un array (respuesta múltiple) o un objeto (respuesta individual)
            if (Array.isArray(result)) {
              // Si por alguna razón devuelve un array para un usuario individual, tomar el primero
              if (result.length > 0) {
                const realUser = result[0]
                console.log(`🔄 Reemplazando usuario temporal con ID real: ${tempUser.id} → ${realUser.id}`)
                
                // IMPORTANTE: Eliminar el registro temporal y guardar el real
                const database = await (await import('../services/database')).initDB()
                await database.delete("dynamicUsers", tempUser.id)
                console.log(`🗑️ Usuario temporal eliminado: ID ${tempUser.id}`)
                
                await addDynamicUserToDB(realUser)
                console.log(`💾 Usuario real guardado: ID ${realUser.id}`)
                
                showToast("success", `Usuario creado exitosamente (ID: ${realUser.id})`)
              } else {
                showToast("error", "No se recibió respuesta del servidor")
              }
            } else {
              // Respuesta individual (objeto DynamicCreateResponse)
              const rawExtra = result.data.extra
              
              const realUser: DynamicUser = {
                id: result.data.id, // ID real del servidor
                folio_client: result.data.folio_client,
                folio: result.data.folio,
                name: result.data.name,
                last_name: result.data.last_name,
                email: result.data.email,
                phone_number: result.data.phone_number,
                printed: result.data.printed,
                print_date: result.data.print_date,
                number_of_prints: result.data.number_of_impressions,
                extra: typeof rawExtra === 'string' ? (rawExtra ? JSON.parse(rawExtra) : {}) : (rawExtra ?? {})
              }

              console.log(`🔄 Reemplazando usuario temporal con ID real: ${tempUser.id} → ${realUser.id}`)

              // IMPORTANTE: Eliminar el registro temporal y guardar el real
              // Esto previene duplicados en IndexedDB
              const database = await (await import('../services/database')).initDB()
              
              // Eliminar registro temporal por ID
              await database.delete("dynamicUsers", tempUser.id)
              console.log(`🗑️ Usuario temporal eliminado: ID ${tempUser.id}`)
              
              // Guardar usuario real con ID del servidor
              await addDynamicUserToDB(realUser)
              console.log(`💾 Usuario real guardado: ID ${realUser.id}`)
              
              showToast("success", `Usuario creado exitosamente (ID: ${result.data.id})`)
            }
          } catch (networkError) {
            console.warn("Error de red, agregando a cola:", networkError)
            await addToQueue({
              type: "create_dynamic_user",
              data: createRequest,
              timestamp: Date.now(),
            })
            showToast("info", "Usuario guardado localmente, se sincronizará cuando haya conexión")
          }
        } else {
          await addToQueue({
            type: "create_dynamic_user",
            data: createRequest,
            timestamp: Date.now(),
          })
          showToast("info", "Usuario guardado localmente, se sincronizará cuando haya conexión")
        }
      }

      // Ejecutar sincronización en segundo plano
      syncCreation().catch(console.error)
      
      showToast("success", "Usuario registrado exitosamente")
    } catch (error) {
      console.error("❌ Error registrando usuario dinámico:", error)
      showToast("error", "Error al registrar el usuario")
    } finally {
      setLoading(false)
    }
  }

  const isFormValid = () => {
    const validationErrors = SchemaService.validateFormData(formData, schema)
    return validationErrors.length === 0
  }

  const renderField = (fieldName: string, fieldConfig: any, _index: number) => {
    const value = formData[fieldName]
    const error = errors[fieldName]
    const isRequired = fieldConfig.required
    const isEditable = fieldConfig.editable !== false

    // Renderizar acordeón
    if (fieldConfig.type === "accordion") {
      const ac = LOCAL_ACCORDIONS.find(a => a.name === fieldName)
      return (
        <div key={fieldName} className="col-span-full">
          <AccordionField label={fieldConfig.label}>
            {ac ? ac.content : "Contenido no disponible."}
          </AccordionField>
        </div>
      )
    }

    return (
      <div key={fieldName}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {fieldConfig.label}{" "}
          {isRequired && <span className="text-red-500">*</span>}
        </label>

        {/* Renderizado según tipo de campo */}
        {fieldConfig.type === 'enum' ? (
          <select
            name={fieldName}
            value={value}
            onChange={handleChange}
            disabled={!isEditable || loading}
            className={`w-full bg-white border-2 border-gray-300 text-gray-800 rounded-xl px-3 py-2 focus:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-300 ${
              error ? "border-red-500/50 bg-red-50/20" : ""
            } ${!isEditable ? "bg-gray-200/30 cursor-not-allowed" : ""}`}
          >
            <option value="">Seleccionar...</option>
            {fieldConfig.options?.map((option: string) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : fieldConfig.type === 'boolean' ? (
          <div className="flex items-center">
            <input
              type="checkbox"
              name={fieldName}
              checked={Boolean(value)}
              onChange={handleChange}
              disabled={!isEditable || loading}
              className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                !isEditable ? "cursor-not-allowed" : ""
              }`}
            />
            <span className="ml-2 text-sm text-gray-600">
              {fieldConfig.label}
            </span>
          </div>
        ) : fieldConfig.type === 'number' ? (
          <input
            type="number"
            name={fieldName}
            value={value}
            onChange={handleChange}
            disabled={!isEditable || loading}
            className={`w-full bg-white border-2 border-gray-300 text-gray-800 rounded-xl px-3 py-2 focus:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-300 ${
              error ? "border-red-500 bg-red-50" : ""
            } ${!isEditable ? "bg-gray-200 cursor-not-allowed" : ""}`}
          />
        ) : (
          <input
            type={fieldName === 'email' ? 'email' : fieldName === 'telefono' ? 'tel' : 'text'}
            name={fieldName}
            value={value}
            onChange={handleChange}
            disabled={!isEditable || loading}
            maxLength={255}
            placeholder={fieldName === 'telefono' ? '+52 555 123 4567' : ''}
            className={`w-full bg-white border-2 border-gray-300 text-gray-800 rounded-xl px-3 py-2 focus:bg-blue-50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-300 placeholder-gray-500 ${
              error ? "border-red-500 bg-red-50" : ""
            } ${!isEditable ? "bg-gray-200 cursor-not-allowed" : ""}`}
          />
        )}

        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white/80 backdrop-blur-md border border-gray-200/50 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white/40 backdrop-blur-sm px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Registro de Usuario Dinámico
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Formulario en 2 columnas */}
              {/* Actualizar el .map() del formulario para usar la lista combinada */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {creationFieldsWithAccordions.map(({ fieldName, fieldSchema }, index) =>
                  renderField(fieldName, fieldSchema, index)
                )}
              </div>

              {/* Advertencia de conexión */}
              {!isOnline && (
                <div className="p-4 rounded-md bg-yellow-50 border border-yellow-200">
                  <div className="flex items-center">
                    <div className="text-yellow-600 mr-2">⚠️</div>
                    <div className="text-sm text-yellow-800">
                      <strong>Sin conexión:</strong> El registro se guardará localmente y se sincronizará cuando haya conexión.
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-500/20 backdrop-blur-sm border border-gray-300/30 text-gray-800 text-sm font-medium rounded-xl hover:bg-gray-500/30 hover:border-gray-400/40 hover:shadow-lg hover:shadow-gray-500/20 transition-all duration-300 disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="inline-flex items-center px-4 py-2 bg-green-600/20 backdrop-blur-sm border border-green-400/30 text-green-800 text-sm font-medium rounded-xl hover:bg-green-600/30 hover:border-green-500/40 hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300 disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      Registrando...
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Registrar Usuario
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicRegisterModal
